import appConfig from 'app-config';
import { userLoggedOut } from 'flow/actions/cti-actions';
import {
    androidNeedAuth,
    iosNeedAuth,
} from 'flow/actions/embedded-app-actions';
import HomeClient from 'flow/api-clients/home-client';
import { setDomainLogoUrls } from 'flow/brands/brands';
import { loggedInUserHasOmnichannelFeature } from 'flow/selectors/extension-selectors';
import { Embedded } from 'flow/types/App';
import EmbeddedType from 'flow/types/embedded-type';
import { AppDispatch } from 'flow/types/Redux';
import { State } from 'flow/types/State';
import {
    addGeneralLogEntry,
    addOmniChannelLogEntry,
    clearAdminLocationFromStorage,
    getAdminLocationFromStorage,
} from 'flow/utils/browser-utils';
import { getCentionUrl } from 'flow/utils/omnichannel-utils';
import { takePendingUaePassLogoutRedirect } from 'flow/utils/uae-pass-logout';
import { history } from 'flow/configure-store';
import isElectron from 'is-electron';
import moment from 'moment';

const SIGNIN_PATH = '/signin.jsp';
const AUTH_TEAMS_PATH = '/auth/teams';
const SIGNOUT_QUERY_PARAM = 'signout';
const REDIRECT_TO_APP_QUERY_PARAM = 'app';
const APP_URL_QUERY_PARAM = 'appUrl';
const RESTRICTED_HOST_SUFFIXES = [
    '.etisalat.ae',
    '.co.uk',
    'voiceflexflow.com',
]; //May need to fix this later, just done this for hotfix

const isRestrictedHost = (hostname: string): boolean => {
    return RESTRICTED_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
};

const disconnectTelavoxApp =
    (embeddedType: Embedded | null | undefined) => (dispatch: AppDispatch) => {
        /* Always consumed so it cannot leak into the next sign-out; only a plain web page may navigate away. */
        const uaePassLogoutUrl = takePendingUaePassLogoutRedirect();

        if (embeddedType === EmbeddedType.IOS) {
            dispatch(iosNeedAuth());
        } else if (embeddedType === EmbeddedType.ANDROID) {
            try {
                dispatch(androidNeedAuth());
            } catch (error) {
                addGeneralLogEntry(
                    `Error when trying to prompt Android auth: ${error}`,
                );
                dispatch(showSignin());
            }
        } else if (
            embeddedType === EmbeddedType.TEAMS ||
            embeddedType === EmbeddedType.TEAMSFV
        ) {
            dispatch(showAuthTeams(window.location.pathname));
        } else if (embeddedType === EmbeddedType.TEAMSFVMANUAL) {
            dispatch(showSignin());
        } else if (embeddedType === EmbeddedType.INTEGRATION) {
            dispatch(showSignin());
            dispatch(userLoggedOut());
        } else if (isElectron()) {
            dispatch(showSignin());
        } else if (uaePassLogoutUrl) {
            /* The iframe could not carry the UAE Pass cookie, so log out top-level; UAE Pass returns to /signin. */
            clearAdminLocationFromStorage();
            window.location.assign(uaePassLogoutUrl);
        } else {
            goToWebappSignin(
                appConfig.production,
                window.location.hostname,
                HomeClient.apiHost,
                dispatch,
                appConfig.authServiceEnabled,
            );
        }
    };

export const goToWebappSignin = (
    isProduction: boolean,
    locationHostName: string,
    apiHost: string,
    dispatch: AppDispatch,
    isAuthServiceEnabled: boolean,
): void => {
    if (
        isProduction &&
        !locationHostName.includes('localhost') &&
        !isAuthServiceEnabled
    ) {
        const saved = getAdminLocationFromStorage();
        const adminPageURL = new URL(
            (saved ? 'https://' + saved : apiHost) + SIGNIN_PATH,
        );
        adminPageURL.searchParams.set(REDIRECT_TO_APP_QUERY_PARAM, 'true');
        // The UAE WAF rejects a URL-valued query param, and UAE has no allowed
        // redirect origins configured, so the param is omitted there.
        if (!isRestrictedHost(locationHostName)) {
            adminPageURL.searchParams.set(
                APP_URL_QUERY_PARAM,
                `https://${locationHostName}`,
            );
        }
        if (saved) {
            adminPageURL.searchParams.set(SIGNOUT_QUERY_PARAM, 'true');
            clearAdminLocationFromStorage();
        }
        window.location.assign(adminPageURL.toString());
    } else {
        dispatch(showSignin());
    }
};

const logoutUserFromOmnichannel =
    () =>
    (_: AppDispatch, getState: () => State): Promise<void> => {
        return new Promise((resolve) => {
            const { omnichannel } = getState();
            const centionUrl = getCentionUrl(getState());
            const omnichannelIframe = omnichannel.get('iframeRef');

            if (omnichannelIframe) {
                omnichannelIframe.addEventListener('load', () => {
                    addOmniChannelLogEntry('Loaded omnichannel logout url');
                    resolve();
                });

                omnichannelIframe.addEventListener('error', (error) => {
                    addOmniChannelLogEntry(
                        `Error loading omnichannel logout url: ${error}`,
                    );
                    resolve();
                });

                addOmniChannelLogEntry('Triggering omnichannel logout url');
                omnichannelIframe.src = `${centionUrl}/ng/logout`;
            } else {
                addOmniChannelLogEntry(
                    'No omnichannel iframe found, not triggering omnichannel logout',
                );
                resolve();
            }
        });
    };

export const disconnectApp =
    (
        embeddedType: Embedded | null | undefined,
        shouldLogoutUserFromOmni = true,
    ) =>
    async (dispatch: AppDispatch, getState: () => State) => {
        const startTime = moment();
        const userIsOmniUser = loggedInUserHasOmnichannelFeature(getState());

        if (userIsOmniUser && shouldLogoutUserFromOmni) {
            await dispatch(logoutUserFromOmnichannel());
        }

        const omniLogoutDuration = moment
            .duration(moment().diff(startTime))
            .asMilliseconds();
        if (omniLogoutDuration > 2000) {
            // This log will probably be sent next time the user logs in
            addGeneralLogEntry(
                `Long omni logout duration causing Telavox client disconnect to be delayed by ${omniLogoutDuration}ms`,
            );
        }

        dispatch(disconnectTelavoxApp(embeddedType));
    };

export const showAuthTeams = (referer: string) => {
    return () => {
        // Only the first 401 knows where the user was - after
        // this push, referer is /auth/teams because the caller
        // passes window.location.pathname
        if (history.location.pathname !== AUTH_TEAMS_PATH) {
            history.push(`${AUTH_TEAMS_PATH}?__ref=${referer}`);
        }
    };
};

export const showSignin = (referer?: string) => {
    setDomainLogoUrls();
    if (referer) {
        return () => history.push(`/signin?__ref=${referer}`);
    } else {
        return () => history.push('/signin');
    }
};

/**
 * Navigates to the given destination (string).
 * Strips 'empty' '/' from links (Such as 'https://foo.bar/test/'
 * to 'https://foo.bar/test')
 */
export const navigateTo = (destination: string) => {
    const destinationArray = destination.split('/').filter((o) => o !== '');
    return () => history.push('/' + destinationArray.join('/'));
};

/**
 * Takes the current path, splits on '/' and removes
 * the last entry, then moves to the path left.
 */
export const navigateBack = (location: { pathname: string }) => {
    const pathname = location?.pathname;
    const backPathArray = pathname?.split('/') || [];
    backPathArray.pop();
    return () => history.push(backPathArray.join('/'));
};

export const navigateToMeetingLobby =
    (meetingCode: string) => (dispatch: AppDispatch) => {
        dispatch(navigateTo(`/video?room=${meetingCode}`));
    };
